import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import apiClient from "@/api/client";

const useFormHandler = ({
    initialValues,
    validations,
    endpoint,
    includeIP = false,
    transformFn
}) => {
    const [formData, setFormData] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (includeIP) {
            axios
                .get("https://api.ipify.org?format=json")
                .then((res) => {
                    setFormData((prev) => ({ ...prev, ip_address: res.data.ip }));
                })
                .catch((err) => {
                    console.error("Failed to get IP:", err);
                });
        }
    }, [includeIP]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const validate = () => {
        const newErrors = {};

        for (const field in validations) {
            if (field === "__checkboxGroup") continue;

            const value = formData[field];
            const rules = validations[field];

            // Required field validation
            if (rules.required && (value === "" || value === undefined || value === null)) {
                newErrors[field] = `${field.replace(/_/g, ' ')} is required`;
                continue; // Skip other validations if required field is empty
            }

            // Skip other validations if field is empty and not required
            if (!value && !rules.required) continue;

            // Pattern validation
            if (rules.pattern && !rules.pattern.test(value)) {
                newErrors[field] = rules.message || `Invalid ${field.replace(/_/g, ' ')}`;
                continue;
            }

            // MinLength validation
            if (rules.minLength && value.length < rules.minLength) {
                newErrors[field] = rules.message || `${field.replace(/_/g, ' ')} must be at least ${rules.minLength} characters`;
                continue;
            }

            // MaxLength validation
            if (rules.maxLength && value.length > rules.maxLength) {
                newErrors[field] = rules.message || `${field.replace(/_/g, ' ')} cannot exceed ${rules.maxLength} characters`;
                continue;
            }

            // Custom validation function
            if (rules.validate) {
                const validationResult = rules.validate(value);
                if (validationResult !== true) {
                    newErrors[field] = validationResult;
                    continue;
                }
            }
        }

        // Special checkbox group validation if defined
        if (validations.__checkboxGroup) {
            const { fields, message } = validations.__checkboxGroup;
            const anyChecked = fields.some((field) => formData[field]);
            if (!anyChecked) {
                newErrors.__checkboxGroup = message || "Please select at least one option";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e, onSuccess) => {
        e.preventDefault();

        if (!validate()) {
            Swal.fire("Validation Error", "Please fix the form errors.", "error");
            return;
        }

        setLoading(true);
        try {
            // Use transformFn if provided, otherwise use formData directly
            const payload = transformFn ? transformFn(formData) : formData;
            // ✅ Use apiClient instead of raw axios
            await apiClient.post(endpoint, payload);

            Swal.fire("Success!", "Form submitted successfully!", "success");

            // Call success callback if provided
            if (onSuccess) {
                onSuccess();
            }

            // Reset form
            setFormData(initialValues);
            setErrors({});
        } catch (error) {
            // Log the full error object to console for debugging
            console.error("An error occurred:", error);

            // Extract a user-friendly message
            const message =
                error.response?.data?.message || error.message || "Something went wrong!";

            // Show user-friendly alert
            Swal.fire("Error", message, "error");
        }

        finally {
            setLoading(false);
        }
    };



    return {
        formData,
        errors,
        loading,
        handleInputChange,
        handleSubmit,
        setFormData,
    };
};

export default useFormHandler;